import {
  areIntervalsOverlapping,
  differenceInMilliseconds,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  getDay,
  Interval,
  isSameDay,
  startOfDay,
  getYear,
  getMonth,
  startOfYear,
  endOfYear,
  differenceInMinutes,
  max,
  min,
  getDayOfYear,
  getDaysInYear,
  setDayOfYear
} from 'date-fns';
import {
  formatInTimeZone,
  toDate,
  utcToZonedTime,
  zonedTimeToUtc
} from 'date-fns-tz';

export class DateUtils {
  private constructor() {}

  public static withCurrentTimezone(): DateUtilsWithTimezone {
    return new DateUtilsWithTimezone(
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
  }

  /**
   *
   * @param timezone
   * @returns
   */
  public static withSpecificTimezone(timezone: string): DateUtilsWithTimezone {
    return new DateUtilsWithTimezone(timezone);
  }
}

export class DateUtilsWithTimezone {
  constructor(private readonly timezone: string) {}

  public formatDateRange(from: DateInput, to: DateInput): string {
    const fromDateString = this.formatDateString(from);
    const fromTimeString = this.formatTimeString(from);
    const toDateString = this.formatDateString(to);
    const toTimeString = this.formatTimeString(to);

    return `${fromDateString} ${fromTimeString} - ${toDateString !== fromDateString ? `${toDateString} ` : ''}${toTimeString}`;
  }

  public formatDateString(input: DateInput): string {
    return this.format(this.getDate(input), 'dd.MM.yyyy');
  }

  public formatTimeString(input: DateInput): string {
    return this.format(this.getDate(input), 'HH:mm');
  }

  public formatDateAndTimeString(input: DateInput): string {
    return this.format(this.getDate(input), 'dd.MM.yyyy HH:mm');
  }

  public formatDayOfTheWeekString(input: DateInput): string {
    return this.format(this.getDate(input), 'ccc');
  }

  /**
   * @returns the day of week, 0 represents Sunday
   */
  public getDayOfWeek(input: DateInput): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
    return getDay(this.getDate(input));
  }

  public isSunday(input: DateInput): boolean {
    return this.getDayOfWeek(input) === 0;
  }

  public createDateFromParts(options: CreateDateFromPartsOptions): Date {
    if (options.isUTC) {
      return new Date(
        Date.UTC(
          options.year,
          options.month - 1,
          options.day,
          options.hours,
          options.minutes,
          options.seconds,
          options.milliseconds
        )
      );
    }
    const date = new Date();
    date.setFullYear(options.year, options.month - 1, options.day ?? 1);
    date.setHours(
      options.hours ?? 0,
      options.minutes ?? 0,
      options.seconds ?? 0,
      options.milliseconds ?? 0
    );
    return zonedTimeToUtc(date, this.timezone);
  }

  public createDateForDayOfYear({
    year,
    dayOfYear
  }: {
    year: number;
    dayOfYear: number;
  }): Date {
    return zonedTimeToUtc(
      setDayOfYear(new Date(year, 1), dayOfYear),
      this.timezone
    );
  }

  public dateRangesOverlap(range1: DateRange, range2: DateRange): boolean {
    return areIntervalsOverlapping(
      this.convertDateRangeToInterval(range1),
      this.convertDateRangeToInterval(range2)
    );
  }

  public getStartOfDay(input: DateInput): Date {
    return this.modifyZonedDate(this.getDate(input), (zonedDate) =>
      startOfDay(zonedDate)
    );
  }

  public getEndOfDay(input: DateInput): Date {
    return this.modifyZonedDate(this.getDate(input), (zonedDate) =>
      endOfDay(zonedDate)
    );
  }

  public getStartOfYear(input: DateInput): Date {
    return this.modifyZonedDate(this.getDate(input), (zonedDate) =>
      startOfYear(zonedDate)
    );
  }

  public getEndOfYear(input: DateInput): Date {
    return this.modifyZonedDate(this.getDate(input), (zonedDate) =>
      endOfYear(zonedDate)
    );
  }

  public getTime(input: DateInput): number {
    return this.getDate(input).getTime();
  }

  public getDate(input: DateInput): Date {
    return toDate(input, { timeZone: this.timezone });
  }

  public getEndOfMonth(input: DateInput): Date {
    return this.modifyZonedDate(this.getDate(input), (zonedDate) =>
      endOfMonth(zonedDate)
    );
  }

  public getDaysInInterval(start: DateInput, end: DateInput): Array<Date> {
    return eachDayOfInterval({
      start: utcToZonedTime(this.getDate(start), this.timezone),
      end: utcToZonedTime(this.getDate(end), this.timezone)
    }).map((d) => zonedTimeToUtc(d, this.timezone));
  }

  public isSameDay(date1: DateInput, date2: DateInput): boolean {
    return isSameDay(
      utcToZonedTime(this.getDate(date1), this.timezone),
      utcToZonedTime(this.getDate(date2), this.timezone)
    );
  }

  public getDiffInMilliseconds(start: DateInput, end: DateInput): number {
    return differenceInMilliseconds(this.getDate(end), this.getDate(start));
  }

  public getDiffInMinutes(start: DateInput, end: DateInput): number {
    return differenceInMinutes(this.getDate(end), this.getDate(start));
  }

  public getYear(date: DateInput): number {
    return this.getValueFromZonedDate(date, (zonedDate) => getYear(zonedDate));
  }

  public getDayOfYear(date: DateInput): number {
    return getDayOfYear(utcToZonedTime(this.getDate(date), this.timezone));
  }

  public getDaysInYear(year: number): number {
    return getDaysInYear(new Date(year, 0, 1));
  }

  /**
   * Retrieves the month of a given date.
   *
   * Months are 1-indexed, so January is represented as `1`, February as
   * `2`, and so on.
   *
   * @example
   * assert(getMonth('2022-04-01') === 4);
   */
  public getMonth(date: DateInput): number {
    return (
      this.getValueFromZonedDate(date, (zonedDate) => getMonth(zonedDate)) + 1
    );
  }

  /**
   * Retrieves the short name of the month provided.
   *
   * @param month number of a month, where January is 1
   *
   * @example
   * assert(getShortNameOfMonth(2) === 'Feb');
   */
  public getShortNameOfMonth(month: number): string {
    const date = this.createDateFromParts({
      year: 2000,
      month
    });

    return this.format(date, 'LLL');
  }

  /**
   * Returns the latest of multiple dates.
   *
   * @example
   * assert(
   *   max([
   *     new Date(1989, 6, 10),
   *     new Date(1987, 1, 11),
   *     new Date(1995, 6, 2),
   *     new Date(1990, 0, 1)
   *   ]) === new Date(1995, 6, 2)
   * );
   */
  public max(dates: Array<DateInput>): Date {
    return max(dates.map((d) => this.getDate(d)));
  }

  /**
   * Returns the earliest of multiple dates.
   *
   * @example
   * assert(
   *   min([
   *     new Date(1989, 6, 10),
   *     new Date(1987, 1, 11),
   *     new Date(1995, 6, 2),
   *     new Date(1990, 0, 1)
   *   ]) === new Date(1987, 1, 11)
   * );
   */
  public min(dates: Array<DateInput>): Date {
    return min(dates.map((d) => this.getDate(d)));
  }

  private convertDateRangeToInterval(range: DateRange): Interval {
    return {
      start: this.getDate(range.start),
      end: this.getDate(range.end)
    };
  }

  private format(date: Date, format: string): string {
    return formatInTimeZone(date, this.timezone, format);
  }

  private modifyZonedDate(date: Date, worker: (zonedDate: Date) => Date): Date {
    return zonedTimeToUtc(
      worker(utcToZonedTime(date, this.timezone)),
      this.timezone
    );
  }

  private getValueFromZonedDate<T extends string | number>(
    date: DateInput,
    worker: (zonedDate: Date) => T
  ): T {
    return worker(utcToZonedTime(this.getDate(date), this.timezone));
  }
}

export type DateInput = string | number | Date;

export type CreateDateFromPartsOptions = {
  year: number;
  /**
   * 1 = January, 2 = February, 3 = March, ...
   */
  month: number;
  /**
   * @default 1
   */
  day?: number;
  /**
   * @default 0
   */
  hours?: number;
  /**
   * @default 0
   */
  minutes?: number;
  /**
   * @default 0
   */
  seconds?: number;
  /**
   * @default 0
   */
  milliseconds?: number;
  /**
   * default false
   *
   * if true, then the values will be seen as if they are in the UTC timezone. Else the current timezone will be used
   */
  isUTC?: boolean;
};

export type DateRange = {
  start: DateInput;
  end: DateInput;
};
